/**
 * AggregatorPanel — plan-admin tool to merge internal universe files with
 * FDR submissions for the same protocol / universe / period, with
 * source attribution, duplicate + conflict detection, aggregate
 * validation, and a normalized .xlsx export.
 *
 * Universe rows are NEVER persisted to the database — only the lineage
 * manifest is written immutably to audit_scrubber_events server-side.
 * Aggregate results live in server memory for 30 minutes.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SCRUBBER_API_BASE } from '../../api/config';
import { csrfFetch } from '../../api/csrf';

const PROTOCOL_UNIVERSES = {
  ODAG: [
    { value: 'odag_table1',                  label: 'Table 1 — Organization Determinations' },
    { value: 'odag_table2_reconsiderations', label: 'Table 2 — Reconsiderations' },
    { value: 'odag_table3_pymt_c',           label: 'Table 3 — Payment OD & Reconsiderations' },
    { value: 'odag_table4_eff_c',            label: 'Table 4 — Part C Effectuations' },
    { value: 'odag_table5_grv_c',            label: 'Table 5 — Part C Grievances' },
  ],
  CDAG: [
    { value: 'cdag_table1_cd',     label: 'Table 1 — Coverage Determinations' },
    { value: 'cdag_table2_cder',   label: 'Table 2 — CD Exception Requests' },
    { value: 'cdag_table3_pymt_d', label: 'Table 3 — Part D Payment' },
    { value: 'cdag_table4_rd',     label: 'Table 4 — Redeterminations' },
    { value: 'cdag_table5_eff_d',  label: 'Table 5 — Part D Effectuations' },
    { value: 'cdag_table6_grv_d',  label: 'Table 6 — Part D Grievances' },
    { value: 'cdag_table7_ar',     label: 'Table 7 — At-Risk Determinations' },
  ],
  FA: [
    { value: 'fa_table1_rcfa', label: 'Table 1 — Rejected Claims (RCFA)' },
    { value: 'fa_table2_rct',  label: 'Table 2 — Rejected Claims Transition (RCT)' },
    { value: 'fa_table3_pde',  label: 'Table 3 — PDE Reconciliation' },
    { value: 'fa_table4_ne',   label: 'Table 4 — Negative Enrollment' },
  ],
  SNPCC: [{ value: 'snpe', label: 'Table 1 — SNPE Enrollee Detail' }],
  CPE:   [{ value: 'cpe_coa', label: 'Table 1 — Corrective Action Analysis' }],
};

export default function AggregatorPanel() {
  const [protocol, setProtocol] = useState('ODAG');
  const [universeType, setUniverseType] = useState('odag_table1');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');

  const [internalFiles, setInternalFiles] = useState([]);  // {file, label}
  const [internalLabel, setInternalLabel] = useState('Internal universe');

  const [availableFdrSubmissions, setAvailableFdrSubmissions] = useState([]);
  const [selectedFdrIds, setSelectedFdrIds] = useState(new Set());
  const [fdrLoading, setFdrLoading] = useState(false);

  const [running, setRunning] = useState(false);
  const [run, setRun] = useState(null);
  const [error, setError] = useState(null);

  const universes = useMemo(() => PROTOCOL_UNIVERSES[protocol] || [], [protocol]);

  function onProtocolChange(next) {
    setProtocol(next);
    const first = (PROTOCOL_UNIVERSES[next] || [])[0];
    setUniverseType(first ? first.value : '');
    setSelectedFdrIds(new Set());
  }

  // Pull FDR submissions matching protocol + universe + period.
  const loadFdrSubmissions = useCallback(async () => {
    if (!universeType) return;
    setFdrLoading(true); setError(null);
    try {
      const params = new URLSearchParams();
      params.set('protocol', protocol);
      params.set('universe_type', universeType);
      if (periodStart) params.set('period_start', periodStart);
      if (periodEnd)   params.set('period_end',   periodEnd);
      const res = await fetch(`${SCRUBBER_API_BASE}/api/fdr/submissions?${params.toString()}`, {
        credentials: 'include',
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        setError(json.message || 'Could not load FDR submissions.');
        return;
      }
      setAvailableFdrSubmissions(json.submissions || []);
    } catch (e) {
      setError(e.message || 'Could not load FDR submissions.');
    } finally {
      setFdrLoading(false);
    }
  }, [protocol, universeType, periodStart, periodEnd]);

  useEffect(() => { loadFdrSubmissions(); }, [loadFdrSubmissions]);

  function addInternalFile(fileList) {
    const next = [...internalFiles];
    for (const f of fileList) next.push({ file: f, label: internalLabel || `Internal: ${f.name}` });
    setInternalFiles(next);
  }

  function toggleFdr(id) {
    setSelectedFdrIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }

  async function runAggregate() {
    if (!periodStart || !periodEnd) { setError('Pick a reporting period.'); return; }
    if (internalFiles.length === 0 && selectedFdrIds.size === 0) {
      setError('Add at least one internal file or select at least one FDR submission.');
      return;
    }
    setRunning(true); setError(null); setRun(null);
    try {
      const fd = new FormData();
      fd.append('universe_type', universeType);
      fd.append('period_start', periodStart);
      fd.append('period_end', periodEnd);
      fd.append('fdr_submission_ids', JSON.stringify([...selectedFdrIds]));
      fd.append('internal_labels', JSON.stringify(internalFiles.map((x) => x.label)));
      for (const x of internalFiles) fd.append('internal_files', x.file, x.file.name);

      const res = await csrfFetch(`${SCRUBBER_API_BASE}/api/aggregate/run`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        setError(json.message || 'Aggregation failed.');
        return;
      }
      setRun(json.run);
    } catch (e) {
      setError(e.message || 'Aggregation failed.');
    } finally {
      setRunning(false);
    }
  }

  function exportAggregate() {
    if (!run) return;
    const url = `${SCRUBBER_API_BASE}/api/aggregate/runs/${encodeURIComponent(run.run_id)}/export`;
    csrfFetch(url, { method: 'POST', credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) throw new Error(await res.text());
        const blob = await res.blob();
        const dl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = dl;
        a.download = `aggregate_${run.universe_type}_${run.run_id}.xlsx`;
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(dl);
      })
      .catch((e) => alert(e.message || 'Export failed'));
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <header className="mb-5">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Universe Aggregator</div>
        <h1 className="text-2xl font-bold mt-1">Combine internal + FDR universes</h1>
        <p className="text-sm text-slate-500 mt-1">
          Universe rows are never stored — files are decrypted in memory, merged, validated, and exposed for export.
        </p>
      </header>

      <section className="bg-white border border-slate-200 rounded-2xl p-5 mb-5">
        <h2 className="font-semibold mb-3 text-sm uppercase tracking-wide text-slate-600">Aggregation target</h2>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Protocol">
            <select value={protocol} onChange={(e) => onProtocolChange(e.target.value)} className={inputCls}>
              {Object.keys(PROTOCOL_UNIVERSES).map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="Universe">
            <select value={universeType} onChange={(e) => setUniverseType(e.target.value)} className={inputCls}>
              {universes.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
            </select>
          </Field>
          <Field label="Period start">
            <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} className={inputCls} />
          </Field>
          <Field label="Period end">
            <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} className={inputCls} />
          </Field>
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-2xl p-5 mb-5">
        <h2 className="font-semibold mb-3 text-sm uppercase tracking-wide text-slate-600">Internal universes</h2>
        <div className="flex gap-3 items-end mb-3">
          <Field label="Label for the next file">
            <input value={internalLabel} onChange={(e) => setInternalLabel(e.target.value)} className={inputCls} />
          </Field>
          <label className="bg-slate-900 hover:bg-slate-700 text-white text-sm font-semibold rounded-lg px-4 py-2 cursor-pointer">
            + Attach file
            <input type="file" accept=".xlsx,.xls" multiple className="hidden"
              onChange={(e) => addInternalFile(e.target.files || [])} />
          </label>
        </div>
        {internalFiles.length === 0
          ? <div className="text-xs text-slate-400">No internal files attached yet.</div>
          : (
            <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
              {internalFiles.map((x, i) => (
                <li key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                  <div>
                    <div className="font-semibold">{x.label}</div>
                    <div className="text-[11px] text-slate-500 font-mono">{x.file.name} · {(x.file.size / 1024).toFixed(1)} KB</div>
                  </div>
                  <button onClick={() => setInternalFiles(internalFiles.filter((_, j) => j !== i))}
                    className="text-rose-600 hover:text-rose-700 text-xs font-semibold">Remove</button>
                </li>
              ))}
            </ul>
          )}
      </section>

      <section className="bg-white border border-slate-200 rounded-2xl p-5 mb-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-sm uppercase tracking-wide text-slate-600">FDR submissions matching this universe</h2>
          <button onClick={loadFdrSubmissions} className="text-xs text-slate-500 underline">Refresh</button>
        </div>
        {fdrLoading && <div className="text-xs text-slate-500">Loading…</div>}
        {!fdrLoading && availableFdrSubmissions.length === 0 && (
          <div className="text-xs text-slate-400">No matching FDR submissions found. Use the FDR Invitations module to invite one.</div>
        )}
        {availableFdrSubmissions.length > 0 && (
          <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
            {availableFdrSubmissions.map((s) => (
              <li key={s.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                <input type="checkbox" checked={selectedFdrIds.has(s.id)} onChange={() => toggleFdr(s.id)} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{s.fdr_organization_name}</div>
                  <div className="text-[11px] text-slate-500 font-mono truncate">{s.file_name} · {s.universe_period_start} → {s.universe_period_end}</div>
                </div>
                <div className="text-[11px] text-slate-400">{new Date(s.submitted_at).toLocaleDateString()}</div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {error && <div className="mb-3 text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded px-3 py-2">{error}</div>}

      <div className="flex gap-3 mb-6">
        <button onClick={runAggregate} disabled={running} className="bg-rose-600 hover:bg-rose-700 disabled:opacity-60 text-white font-semibold px-5 py-2.5 rounded-lg text-sm">
          {running ? 'Aggregating…' : 'Run aggregation'}
        </button>
        {run && (
          <button onClick={exportAggregate} className="bg-slate-900 hover:bg-slate-700 text-white font-semibold px-5 py-2.5 rounded-lg text-sm">
            Export aggregate (.xlsx)
          </button>
        )}
      </div>

      {run && <AggregateResults run={run} />}
    </div>
  );
}

function AggregateResults({ run }) {
  const [view, setView] = useState('rows');
  return (
    <section className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Run {run.run_id.slice(0, 8)}…</div>
          <div className="font-semibold">{run.universe_type} · {run.period.startDate} → {run.period.endDate}</div>
          <div className="text-xs text-slate-500 mt-1">
            {run.sources.length} sources · {run.total_rows} rows ·{' '}
            <span className={run.validation.error_count === 0 ? 'text-emerald-600 font-semibold' : 'text-amber-600 font-semibold'}>
              {run.validation.error_count} validation issues
            </span>{' · '}
            <span className={run.duplicate_groups === 0 ? 'text-slate-500' : 'text-amber-600 font-semibold'}>
              {run.duplicate_groups} duplicate groups
            </span>{' · '}
            <span className={run.conflict_groups === 0 ? 'text-slate-500' : 'text-rose-600 font-semibold'}>
              {run.conflict_groups} cross-source conflicts
            </span>
          </div>
        </div>
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
          {['rows', 'sources', 'duplicates', 'conflicts', 'validation'].map((k) => (
            <button key={k} onClick={() => setView(k)}
              className={`px-3 py-1 text-xs font-semibold rounded ${view === k ? 'bg-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
              {k}
            </button>
          ))}
        </div>
      </div>

      {view === 'sources' && (
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-600 uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2 font-semibold">Kind</th>
              <th className="text-left px-4 py-2 font-semibold">Label</th>
              <th className="text-left px-4 py-2 font-semibold">File</th>
              <th className="text-left px-4 py-2 font-semibold">Hash</th>
              <th className="text-left px-4 py-2 font-semibold">Rows</th>
              <th className="text-left px-4 py-2 font-semibold">Fingerprint</th>
              <th className="text-left px-4 py-2 font-semibold">Issues</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {run.per_source_summary.map((s, i) => (
              <tr key={i} className="hover:bg-slate-50">
                <td className="px-4 py-2"><Badge color={s.kind === 'fdr' ? 'rose' : 'slate'}>{s.kind}</Badge></td>
                <td className="px-4 py-2 font-semibold">{s.label}</td>
                <td className="px-4 py-2 font-mono">{s.file_name}</td>
                <td className="px-4 py-2 font-mono text-[10px]">{(s.file_hash || '').slice(0, 12)}…</td>
                <td className="px-4 py-2">{s.raw_row_count}</td>
                <td className="px-4 py-2">
                  {s.fingerprint
                    ? <span className={s.fingerprint.matches_target ? 'text-emerald-600' : 'text-amber-600'}>
                        {s.fingerprint.best_match} · {(s.fingerprint.score * 100).toFixed(0)}%
                      </span>
                    : '—'}
                </td>
                <td className="px-4 py-2">
                  {s.parse_error && <span className="text-rose-600">parse: {s.parse_error}</span>}
                  {(s.missing_columns || []).length > 0 && <div className="text-amber-600">missing: {s.missing_columns.length}</div>}
                  {(s.dropped_columns || []).length > 0 && <div className="text-slate-500">dropped: {s.dropped_columns.length}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {view === 'rows' && (
        <div className="overflow-auto max-h-[60vh]">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-600 uppercase tracking-wide sticky top-0">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">#</th>
                <th className="text-left px-3 py-2 font-semibold">Source</th>
                {run.canonical_headers.map((h) => <th key={h} className="text-left px-3 py-2 font-semibold whitespace-nowrap">{h}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {run.rows.slice(0, 500).map((r) => (
                <tr key={r.__row_index} className="hover:bg-slate-50">
                  <td className="px-3 py-1 text-slate-400">{r.__row_index}</td>
                  <td className="px-3 py-1 whitespace-nowrap">
                    <Badge color={r.__source.kind === 'fdr' ? 'rose' : 'slate'}>{r.__source.label}</Badge>
                  </td>
                  {r.values.map((v, i) => <td key={i} className="px-3 py-1 font-mono whitespace-nowrap">{v == null ? '' : String(v)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
          {run.rows.length > 500 && (
            <div className="px-4 py-3 text-xs text-slate-500">
              Showing first 500 of {run.rows.length} rows. Use “Export aggregate (.xlsx)” for the full set.
            </div>
          )}
        </div>
      )}

      {view === 'duplicates' && (
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-600 uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2 font-semibold">Composite key</th>
              <th className="text-left px-4 py-2 font-semibold">Occurrences</th>
              <th className="text-left px-4 py-2 font-semibold">Cross-source?</th>
              <th className="text-left px-4 py-2 font-semibold">Sources</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {run.duplicates.map((d, i) => (
              <tr key={i} className="hover:bg-slate-50">
                <td className="px-4 py-2 font-mono">{d.composite_key}</td>
                <td className="px-4 py-2">{d.occurrences}</td>
                <td className="px-4 py-2">{d.cross_source ? <span className="text-amber-600 font-semibold">Yes</span> : 'No'}</td>
                <td className="px-4 py-2">{d.sources.join(' · ')}</td>
              </tr>
            ))}
            {run.duplicates.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-slate-400">No duplicates detected.</td></tr>}
          </tbody>
        </table>
      )}

      {view === 'conflicts' && (
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-600 uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2 font-semibold">Composite key</th>
              <th className="text-left px-4 py-2 font-semibold">Field</th>
              <th className="text-left px-4 py-2 font-semibold">Values</th>
              <th className="text-left px-4 py-2 font-semibold">Sources</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {run.conflicts.flatMap((c, i) => c.conflicting_fields.map((f, j) => (
              <tr key={`${i}.${j}`} className="hover:bg-slate-50">
                <td className="px-4 py-2 font-mono">{c.composite_key}</td>
                <td className="px-4 py-2 font-semibold">{f.field}</td>
                <td className="px-4 py-2 font-mono">{f.values.join(' || ')}</td>
                <td className="px-4 py-2">{c.sources.join(' · ')}</td>
              </tr>
            )))}
            {run.conflicts.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-slate-400">No cross-source conflicts.</td></tr>}
          </tbody>
        </table>
      )}

      {view === 'validation' && (
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-600 uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2 font-semibold">Row</th>
              <th className="text-left px-4 py-2 font-semibold">Field</th>
              <th className="text-left px-4 py-2 font-semibold">Category</th>
              <th className="text-left px-4 py-2 font-semibold">Message</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(run.validation.errors_sample || []).map((e, i) => (
              <tr key={i} className="hover:bg-slate-50">
                <td className="px-4 py-2">{e.row}</td>
                <td className="px-4 py-2 font-mono">{e.field}</td>
                <td className="px-4 py-2"><Badge color={e.category === 'Timeliness' ? 'rose' : 'slate'}>{e.category}</Badge></td>
                <td className="px-4 py-2">{e.message}</td>
              </tr>
            ))}
            {(run.validation.errors_sample || []).length === 0 && (
              <tr><td colSpan={4} className="px-4 py-6 text-center text-emerald-600 font-semibold">Aggregate passes all checks.</td></tr>
            )}
          </tbody>
        </table>
      )}
    </section>
  );
}

const inputCls = 'w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500';

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold text-slate-600 mb-1">{label}</div>
      {children}
    </label>
  );
}

function Badge({ color = 'slate', children }) {
  const map = {
    slate: 'bg-slate-100 text-slate-700 border-slate-200',
    rose:  'bg-rose-100 text-rose-700 border-rose-200',
    emerald: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-100 text-amber-700 border-amber-200',
  };
  return (
    <span className={`inline-block text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded border ${map[color] || map.slate}`}>
      {children}
    </span>
  );
}
